<template>
  <div>
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Vendor Categories</h5>
        <p class="page-sub">Organise vendors by category for easier filtering and reporting</p>
      </div>
      <div class="header-actions">
        <button class="btn-outline" @click="$router.push($url('/procurement/vendors'))">
          <font-awesome-icon icon="arrow-left" class="me-1" /> Back to Vendors
        </button>
        <button
          v-if="permission?.manage_procurement_categories"
          class="btn-primary"
          @click="openCreate"
        >
          <font-awesome-icon icon="plus" class="me-1" /> Add Category
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading categories...</p>
      </div>

      <div v-else-if="!categories.length" class="empty-state">
        <font-awesome-icon icon="tags" class="empty-icon" />
        <p>No categories yet</p>
        <span v-if="permission?.manage_procurement_categories">
          Add your first category to start organising vendors
        </span>
      </div>

      <table v-else class="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Description</th>
            <th>Vendors</th>
            <th v-if="permission?.manage_procurement_categories"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(cat, i) in categories" :key="cat.id">
            <td class="td-num">{{ i + 1 }}</td>
            <td class="cat-name">{{ cat.name }}</td>
            <td class="cat-desc">{{ cat.description || '—' }}</td>
            <td>
              <span class="count-badge">{{ cat.vendors_count ?? 0 }}</span>
            </td>
            <td v-if="permission?.manage_procurement_categories">
              <div class="action-menu">
                <button class="btn-icon" @click="openEdit(cat)" title="Edit">
                  <font-awesome-icon icon="edit" />
                </button>
                <button class="btn-icon btn-icon--danger" @click="confirmDelete(cat)" title="Delete">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create / Edit Modal -->
    <div v-if="modalVisible" class="modal-backdrop" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <h6>{{ editingId ? 'Edit Category' : 'Add Category' }}</h6>
          <button class="btn-close-modal" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="field-group">
            <label class="field-label">Name <span class="req">*</span></label>
            <input
              v-model="form.name"
              class="field-input"
              :class="{ 'field-input--error': errors.name }"
              placeholder="e.g. IT & Software, Raw Materials"
              @keyup.enter="saveCategory"
            />
            <span class="field-error" v-if="errors.name">{{ errors.name }}</span>
          </div>
          <div class="field-group mt-14">
            <label class="field-label">Description</label>
            <textarea
              v-model="form.description"
              class="field-input"
              rows="3"
              placeholder="Optional description..."
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="closeModal">Cancel</button>
          <button class="btn-primary" :disabled="saving" @click="saveCategory">
            <span v-if="saving" class="spinner-sm"></span>
            {{ saving ? 'Saving...' : (editingId ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirm Modal -->
    <div v-if="deleteTarget" class="modal-backdrop" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6>Delete Category</h6>
          <button class="btn-close-modal" @click="deleteTarget = null">&times;</button>
        </div>
        <div class="modal-body">
          <p>
            Delete <strong>{{ deleteTarget.name }}</strong>?
            Vendors assigned to this category will not be deleted, but the category link will be removed.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" :disabled="deleting" @click="deleteCategory">
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';

export default {
  name: 'VendorCategories',

  setup() {
    return { toast: useToast(), store: useStore() };
  },

  data() {
    return {
      categories: [],
      loading: false,

      modalVisible: false,
      editingId: null,
      saving: false,
      errors: {},
      form: { name: '', description: '' },

      deleteTarget: null,
      deleting: false,
    };
  },

  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
  },

  mounted() {
    if (!this.permission?.view_procurement_categories && !this.permission?.manage_procurement_categories) {
      this.toast.error("You don't have permission to view procurement categories");
      this.$router.push(this.$url('/procurement/vendors'));
      return;
    }
    this.fetchCategories();
  },

  methods: {
    async fetchCategories() {
      this.loading = true;
      try {
        const { data } = await axios.get('/procurement/categories');
        this.categories = data.data;
      } catch {
        this.toast.error('Failed to load categories');
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      this.editingId = null;
      this.form = { name: '', description: '' };
      this.errors = {};
      this.modalVisible = true;
    },

    openEdit(cat) {
      this.editingId = cat.id;
      this.form = { name: cat.name, description: cat.description || '' };
      this.errors = {};
      this.modalVisible = true;
    },

    closeModal() {
      this.modalVisible = false;
    },

    async saveCategory() {
      this.errors = {};
      if (!this.form.name.trim()) { this.errors.name = 'Category name is required'; return; }
      this.saving = true;
      try {
        if (this.editingId) {
          await axios.put(`/procurement/categories/${this.editingId}`, this.form);
          this.toast.success('Category updated');
        } else {
          await axios.post('/procurement/categories', this.form);
          this.toast.success('Category created');
        }
        this.closeModal();
        this.fetchCategories();
      } catch (e) {
        const msg = e.response?.data?.message;
        if (e.response?.status === 403) {
          this.toast.error("You don't have permission to manage categories");
        } else {
          this.toast.error(msg || 'Failed to save category');
        }
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(cat) {
      this.deleteTarget = cat;
    },

    async deleteCategory() {
      this.deleting = true;
      try {
        await axios.delete(`/procurement/categories/${this.deleteTarget.id}`);
        this.toast.success('Category deleted');
        this.deleteTarget = null;
        this.fetchCategories();
      } catch (e) {
        if (e.response?.status === 403) {
          this.toast.error("You don't have permission to manage categories");
        } else {
          this.toast.error('Failed to delete category');
        }
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.page-header    { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title     { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub       { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }

.card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

.loading-state, .empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; color:#7a90b0; gap:10px; }
.empty-icon { font-size:40px; color:#dde5f0; }
.empty-state p { font-size:15px; font-weight:600; color:#1a2b45; margin:0; }
.empty-state span { font-size:13px; }

.data-table { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; }
.data-table td { padding:13px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.data-table tbody tr:last-child td { border-bottom:none; }
.data-table tbody tr:hover td { background:#f8fafc; }

.td-num   { color:#7a90b0; font-size:12px; width:44px; }
.cat-name { font-weight:600; }
.cat-desc { color:#7a90b0; font-size:12.5px; }
.count-badge { display:inline-block; background:#f0f5ff; color:#004AAD; font-size:12px; font-weight:600; border-radius:12px; padding:2px 10px; }

.action-menu { display:flex; gap:6px; }
.btn-icon    { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:all .15s; }
.btn-icon:hover { background:#f4f7fb; border-color:#c5d0e0; }
.btn-icon--danger:hover { background:#fee2e2; border-color:#fecaca; color:#dc2626; }

.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box      { background:#fff; border-radius:14px; width:100%; max-width:480px; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm  { max-width:420px; }
.modal-header   { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
.modal-header h6 { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.btn-close-modal { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; line-height:1; }
.modal-body     { padding:24px; }
.modal-footer   { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; }

.field-group { display:flex; flex-direction:column; gap:5px; }
.field-label { font-size:13px; font-weight:600; color:#1a2b45; }
.req         { color:#e03535; }
.field-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; font-family:inherit; box-sizing:border-box; transition:border-color .15s; resize:vertical; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-input--error { border-color:#e03535; }
.field-error { font-size:11.5px; color:#e03535; }
.mt-14 { margin-top:14px; }

.me-1 { margin-right:4px; }

.btn-primary  { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline  { display:inline-flex; align-items:center; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.btn-danger   { display:inline-flex; align-items:center; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; }
.btn-danger:hover:not(:disabled) { background:#b91c1c; }
.btn-danger:disabled { opacity:.5; cursor:not-allowed; }

.spinner    { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
.spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
@keyframes spin { to { transform:rotate(360deg); } }
</style>
